import {
  Box,
  ButtonBase,
  Popover,
  Tooltip,
  styled,
  useTheme,
} from "@mui/material";
import PropTypes from 'prop-types';
import { Palette, FormatColorFill } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import {
  CircleFilledIcon,
  CircleIcon,
  ClearWhiteboardIcon,
  PanAroundIcon,
  PencilIcon,
  PointerIcon,
  SaveImageIcon,
  ShapeIcon,
  SquareIcon,
  TextIcon,
  UndoIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from "../../icons";
import { appThemes, useMeetingAppContext } from "../../MeetingAppContextDef";
import { SketchPicker } from "react-color";
import UploadImageIcon from "../../icons/UploadImageIcon";

const getBackgroundColor = (hoverEffect, appTheme, appThemes) => {
  if (!hoverEffect) return "transparent";

  if (appTheme === appThemes.LIGHT || appTheme === appThemes.DARK) {
    return "#596BFF33";
  } else {
    return "#D5E8FF";
  }
};


const CustomPopover = styled(Popover)`
  & .MuiPaper-root {
    background-color: transparent !important;
    box-shadow: none !important;
  }

  & .MuiPopover-root {
    background-color: transparent !important;
    box-shadow: none !important;
  }
`;

const ToolBarIcon = ({ Icon, onClick, title, isSelected , hoverEffect = false  }) => {
  // const classes = useStyles();
  const theme = useTheme();
  const { appTheme } = useMeetingAppContext();


  return (
    <Tooltip title={title} arrow placement="right">
      <ButtonBase
        color="inherit"
        sx={{
          backgroundColor: isSelected
            ? appTheme === appThemes.LIGHT || appTheme === appThemes.DARK
              ? "#596BFF33"
              : appTheme === appThemes.LIGHT || appTheme === appThemes.DARK
              ? "#596BFF33"
              : "#D5E8FF"
            : "",
          borderRadius: "0.5vh",
          padding: "0.75vh",
          marginTop: "0.2vh",
          marginBottom: "0.2vh",
          "&:hover": {
            backgroundColor: getBackgroundColor(hoverEffect, appTheme, appThemes), 
          },
        }}
        onClick={onClick}
      >
        <Icon
          width="2vh"
          height="2vh"
          viewBox="0 0 24 24"
          style={{
            width: "2vh",
            height: "2vh",
            display: "block",
            flexShrink: 0,
          }}
          fill={
            isSelected
              ? appTheme === appThemes.LIGHT || appTheme === appThemes.DARK
                ? theme.palette.lightTheme.primaryMain
                : theme.palette.primary.main
              : "black"
          }
        />
      </ButtonBase>
    </Tooltip>
  );
};
ToolBarIcon.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  onClick: PropTypes.func,
  title: PropTypes.string,
  isSelected: PropTypes.bool,
  hoverEffect: PropTypes.bool,  // ✅ Add this line
};

ToolBarIcon.defaultProps = {
  onClick: () => {},
  title: '',
  isSelected: false,
  hoverEffect: false,
};

const CustomColorPicker = ({
  title,
  colorPicker,
  setColorPicker,
  color,
  setColor,
  setParentColor,
  changeCanvasBackgroundColor,
  changeBrushColor,
  whiteboardToolbarWidth,
  whiteboardSpacing,
  Icon,
}) => {
  const { appTheme } = useMeetingAppContext();
  const theme = useTheme();

  return (
    <>
      <Tooltip title={title} arrow placement="right">
        <ButtonBase
          // className={classes.btnTool}
          color="inherit"
          component="span"
          onClick={(e) => {
            setColorPicker(e.currentTarget);
          }}
          style={{
            backgroundColor:
              appTheme === appThemes.LIGHT ? "#596BFF33" : `#D5E8FF`,
            borderRadius: "0.5vh",
            padding: "0.75vh",
            marginTop: "0.2vh",
            marginBottom: "0.2vh",
          }}
        >
          <Icon
            height="2vh"
            width="2vh"
            style={{ height: "2vh", width: "2vh", color }}
            fill={color}
          />
        </ButtonBase>
      </Tooltip>

      <CustomPopover
        open={Boolean(colorPicker)}
        anchorEl={colorPicker}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={() => {
          setColorPicker(null);
        }}
        disableRestoreFocus
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "transparent",
            paddingLeft: `calc(${whiteboardToolbarWidth} + ${whiteboardSpacing})`,
            paddingRight: whiteboardSpacing,
            paddingBottom: whiteboardSpacing,
          }}
        >
          <Box
            style={{
              backgroundColor: "white",
              boxShadow: "0 0.46vh 0.93vh #00000029",
            }}
          >
            <SketchPicker
              color={color}
              onChange={(ev) => {
                setColor(ev.hex);
                if (changeBrushColor) {
                  changeBrushColor(ev.hex);
                }
              }}
              onChangeComplete={(ev) => {
                setParentColor(ev.hex);
                if (changeCanvasBackgroundColor) {
                  changeCanvasBackgroundColor(ev.hex);
                }
              }}
            />
          </Box>
        </Box>
      </CustomPopover>
    </>
  );
};

const CustomImagePicker = ({ addImage, setTool }) => {
  const imageInputRef = useRef();
  const theme = useTheme();

  return (
    <>
      <Tooltip title="Add Image" arrow placement="right">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <div
            style={{
              width: "3.5vh",
              height: "3.5vh",
              overflow: "hidden",
              display: "inline-block",
            }}
          >
            <ButtonBase
              color="inherit"
              style={{
                borderRadius: "0.5vh",
                position: "relative",
                cursor: "pointer",
                padding: "0.75vh",
                marginTop: "0.2vh",
                marginBottom: "0.2vh",
              }}
              onClick={() => {
                imageInputRef.current.click();
                setTool("select");
              }}
            >
              <input
                ref={imageInputRef}
                name="image-input"
                type="file"
                accept="image/*"
                onChange={addImage}
                style={{
                  position: "absolute",
                  opacity: 0,
                  height: 0,
                  width: 0,
                }}
              />
              <UploadImageIcon 
                width="2vh"
                height="2vh"
                style={{ width: "2vh", height: "2vh", display: "block" }}
              />
            </ButtonBase>
          </div>
        </div>
      </Tooltip>
    </>
  );
};

const WBToolbar = ({
  setTool,
  downloadCanvas,
  clearCanvas,
  changeCanvasBackgroundColor,
  changeBrushColor,
  undo,
  zoomOut,
  zoomIn,
  tool,
  color: parentColor,
  setColor: setParentColor,
  canvasBackgroundColor: parentCanvasBackgroundColor,
  setCanvasBackgroundColor: setParentCanvasBackgroundColor,
  whiteboardToolbarWidth,
  whiteboardSpacing,
  addImage,
}) => {

  const [color, setColor] = useState(parentColor);
  const [canvasBackgroundColor, setCanvasBackgroundColor] = useState(
    parentCanvasBackgroundColor
  );

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [colorPicker, setColorPicker] = useState(null);
  const [backgroundColorPicker, setBackgroundColorPicker] = useState(null);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const theme = useTheme();

  const open = Boolean(anchorEl);

  const { canDrawOnWhiteboard, appTheme, appMeetingLayout } = useMeetingAppContext();

  useEffect(() => {
    setColor(parentColor);
    setCanvasBackgroundColor(parentCanvasBackgroundColor);
  }, [parentColor, parentCanvasBackgroundColor]);

  const isSpecialLayout = (appMeetingLayout.type === "SIDEBAR" && appMeetingLayout.priority === "SPEAKER") ||
                          (appMeetingLayout.type === "SPOTLIGHT" && appMeetingLayout.priority === "SPEAKER") ||
                          (appMeetingLayout.type === "SPOTLIGHT" && appMeetingLayout.priority === "PIN") ||
                          (appMeetingLayout.type === "SIDEBAR" && appMeetingLayout.priority === "PIN");

  return (
    canDrawOnWhiteboard && (
      <Box
        style={{
          position: isSpecialLayout ? "relative" : "absolute",
          top: isSpecialLayout ? "auto" : whiteboardSpacing,
          left: isSpecialLayout ? "auto" : whiteboardSpacing,
          bottom: isSpecialLayout ? "auto" : whiteboardSpacing,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "white",
          boxShadow: "0 0.46vh 0.93vh #00000029",
          alignItems: "center",
          width: whiteboardToolbarWidth,
          height: isSpecialLayout ? "100%" : "auto",
          overflow: "auto",
          zIndex: 1000,
        }}
      >
        <ToolBarIcon
          {...{
            Icon: PointerIcon,
            onClick: () => setTool("select"),
            title: "Select object",
            isSelected: tool === "select",
            whiteboardToolbarWidth,
          }}
        />
        <ToolBarIcon
          {...{
            Icon: PencilIcon,
            onClick: () => setTool("pencil"),
            title: "Pen",
            isSelected: tool === "pencil",
            whiteboardToolbarWidth,
          }}
        />
        <ToolBarIcon
          {...{
            Icon: TextIcon,
            onClick: () => setTool("text"),
            title: "Text",
            isSelected: tool === "text",
            hoverEffect:true,
            whiteboardToolbarWidth,
          }}
        />
        <>
          <ButtonBase
            onMouseEnter={handlePopoverOpen}
            style={{
              backgroundColor:
                tool === "square" ||
                tool === "squareFilled" ||
                tool === "circle" ||
                tool === "circleFilled"
                  ? appTheme === appThemes.LIGHT || appTheme === appThemes.DARK
                    ? "#596BFF33"
                    : "#D5E8FF"
                  : "",
              borderRadius: "0.5vh",
              padding: "0.75vh",
              marginTop: "0.2vh",
              marginBottom: "0.2vh",
            }}
          >
            {tool === "square" ? (
              <SquareIcon
                width="2vh"
                height="2vh"
                viewBox="0 0 24 24"
                style={{ width: "2vh", height: "2vh", display: "block" }}
                fill={
                  appTheme === appThemes.LIGHT || appTheme === appThemes.DARK
                    ? theme.palette.lightTheme.primaryMain
                    : theme.palette.primary.main
                }
              />
            ) : tool === "squareFilled" ? (
              <ShapeIcon
                width="2vh"
                height="2vh"
                viewBox="0 0 24 24"
                style={{ width: "2vh", height: "2vh", display: "block" }}
                fill={
                  appTheme === appThemes.LIGHT || appTheme === appThemes.DARK
                    ? theme.palette.lightTheme.primaryMain
                    : theme.palette.primary.main
                }
              />
            ) : tool === "circle" ? (
              <CircleIcon
                width="2vh"
                height="2vh"
                viewBox="0 0 24 24"
                style={{ width: "2vh", height: "2vh", display: "block" }}
                fill={
                  appTheme === appThemes.LIGHT || appTheme === appThemes.DARK
                    ? theme.palette.lightTheme.primaryMain
                    : theme.palette.primary.main
                }
              />
            ) : tool === "circleFilled" ? (
              <CircleFilledIcon
                width="2vh"
                height="2vh"
                viewBox="0 0 24 24"
                style={{ width: "2vh", height: "2vh", display: "block" }}
                fill={
                  appTheme === appThemes.LIGHT || appTheme === appThemes.DARK
                    ? theme.palette.lightTheme.primaryMain
                    : theme.palette.primary.main
                }
              />
            ) : (
              <ShapeIcon 
                width="2vh"
                height="2vh"
                viewBox="0 0 24 24"
                style={{ width: "2vh", height: "2vh", display: "block" }}
                fill={"#000"} 
              />
            )}
          </ButtonBase>

          <CustomPopover
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "transparent",
                paddingLeft: `calc(${whiteboardToolbarWidth} + ${whiteboardSpacing})`,
                paddingRight: whiteboardSpacing,
                paddingBottom: whiteboardSpacing,
              }}
              onMouseLeave={handlePopoverClose}
            >
              <Box
                style={{
                  backgroundColor: "white",
                  boxShadow: "0px 5px 10px #00000029",
                }}
              >
                <Box style={{ display: "flex", flexDirection: "row" }}>
                  <ToolBarIcon
                    {...{
                      Icon: SquareIcon,
                      onClick: () => setTool("square"),
                      title: "Square",
                      isSelected: tool === "square",
                      hoverEffect:true,
                      whiteboardToolbarWidth,
                    }}
                  />
                  <ToolBarIcon
                    {...{
                      Icon: ShapeIcon,
                      onClick: () => setTool("squareFilled"),
                      title: "Square Filled",
                      isSelected: tool === "squareFilled",
                      hoverEffect:true,
                      whiteboardToolbarWidth,
                    }}
                  />
                </Box>
                <Box style={{ display: "flex", flexDirection: "row" }}>
                  <ToolBarIcon
                    {...{
                      Icon: CircleIcon,
                      onClick: () => setTool("circle"),
                      title: "Circle",
                      isSelected: tool === "circle",
                      hoverEffect:true,
                      whiteboardToolbarWidth,
                    }}
                  />
                  <ToolBarIcon
                    {...{
                      Icon: CircleFilledIcon,
                      onClick: () => setTool("circleFilled"),
                      title: "Circle Filled",
                      isSelected: tool === "circleFilled",
                      hoverEffect:true,
                      whiteboardToolbarWidth,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </CustomPopover>
        </>
        <ToolBarIcon
          {...{
            Icon: PanAroundIcon,
            onClick: () => setTool("pan"),
            title: "Pan around",
            isSelected: tool === "pan",
            hoverEffect:true,
            whiteboardToolbarWidth,
          }}
        />

        <CustomColorPicker
          {...{
            title: "Choose Color",
            colorPicker: colorPicker,
            setColorPicker: setColorPicker,
            setColor: setColor,
            color: color,
            setParentColor: setParentColor,
            whiteboardToolbarWidth: whiteboardToolbarWidth,
            changeBrushColor: changeBrushColor,
            whiteboardSpacing: whiteboardSpacing,
            Icon: FormatColorFill,
          }}
        />

        <ToolBarIcon
          {...{
            Icon: ZoomInIcon,
            onClick: () => {
              // setTool("zoom in")
              zoomIn();
            },
            title: "Zoom In",
            isSelected: tool === "zoom in",
            hoverEffect:true,
            whiteboardToolbarWidth,
          }}
        />
        <ToolBarIcon
          {...{
            Icon: ZoomOutIcon,
            onClick: () => {
            //  setTool("zoom out")
              zoomOut();
            },
            title: "Zoom Out",
            isSelected: tool === "zoom out",
            hoverEffect:true,
            whiteboardToolbarWidth,
          }}
        />
        <CustomColorPicker
          {...{
            title: "Choose background",
            colorPicker: backgroundColorPicker,
            setColorPicker: setBackgroundColorPicker,
            setColor: setCanvasBackgroundColor,
            color: canvasBackgroundColor,
            setParentColor: setParentCanvasBackgroundColor,
            changeCanvasBackgroundColor: changeCanvasBackgroundColor,
            whiteboardToolbarWidth: whiteboardToolbarWidth,
            whiteboardSpacing: whiteboardSpacing,
            Icon: Palette,
          }}
        />
        <CustomImagePicker addImage={addImage} setTool={setTool} />
        <ToolBarIcon
          {...{
            Icon: UndoIcon,
            onClick: () => {
              // setTool("undo")
              undo()
            },
            title: "Undo last drawing",
            isSelected: tool === "undo",
            hoverEffect:true,
            whiteboardToolbarWidth,
          }}
        />
        <ToolBarIcon
          {...{
            Icon: ClearWhiteboardIcon,
            onClick: () => clearCanvas(),
            title: "Clear whiteboard",
            isSelected: tool === "clear",
            hoverEffect:true,
            whiteboardToolbarWidth,
          }}
        />
        <ToolBarIcon
          {...{
            Icon: SaveImageIcon,
            onClick: () => downloadCanvas(),
            title: "Save as image",
            isSelected: tool === "save",
            hoverEffect:true,
            whiteboardToolbarWidth,
          }}
        />
      </Box>
    )
  );
};

export default WBToolbar;
